import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowPathRoundedSquareIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import AuthShell from '../components/AuthShell';
import LoadingSpinner from '../components/LoadingSpinner';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

function getPostPasswordSetupRedirect(user: { role: 'user' | 'admin'; is_support_staff?: boolean }): string {
  if (user.is_support_staff) {
    return '/support/tenant-switcher';
  }

  return user.role === 'admin' ? '/admin' : '/';
}

function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { establishSession } = useAuth();
  const branding = useBranding();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const heroFeatures = useMemo(
    () => [
      {
        title: 'Recover access',
        description: 'Reset operator credentials without leaving the secure product flow.',
      },
      {
        title: 'Resume fast',
        description: 'Successful password setup signs the user in and returns them to their correct surface.',
      },
      {
        title: 'Stay aligned',
        description: 'Uses the same auth system and mobile spacing as sign-in and registration.',
      },
    ],
    [],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!token) {
      toast.error('Reset token is missing');
      return;
    }

    setIsLoading(true);
    try {
      const response = await authAPI.resetPassword(token, password, confirmPassword);
      const accessToken = response.data?.access_token;

      if (!accessToken) {
        throw new Error('Password reset completed without a session token');
      }

      const user = establishSession(accessToken);
      toast.success('Password updated. You are now signed in.');
      navigate(getPostPasswordSetupRedirect(user), { replace: true });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Unable to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      appName={branding.app_name}
      logoUrl={branding.logo_url}
      headerBadge="Password recovery"
      headerIcon={<ShieldCheckIcon className="h-4 w-4 text-[var(--primary)]" />}
      title="Set a new password"
      description="Choose a new password for your operator account and continue securely back into the platform."
      heroBadge="Credential recovery"
      heroIcon={<ArrowPathRoundedSquareIcon className="h-4 w-4 text-[var(--primary)]" />}
      heroTitle="Restore account access without dropping out of the operator flow."
      heroDescription={`Use ${branding.app_name} password recovery to re-establish secure access and continue directly into the correct tenant or admin surface.`}
      heroFeatures={heroFeatures}
      accentClassName="bg-[radial-gradient(circle,_rgba(14,165,233,0.18),_transparent_70%)]"
    >
      {!token ? (
        <div className="auth-subpanel mt-6 p-4 text-sm leading-6 text-on-surface-variant">
          This reset link is missing a token. Request a new one from the login page.
        </div>
      ) : (
        <form
          className="mt-6 space-y-5 sm:mt-8"
          name="operator-password-reset"
          autoComplete="on"
          onSubmit={handleSubmit}
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="new-password" className="mb-1 block text-sm font-medium text-on-surface">
                New password
              </label>
              <input
                id="new-password"
                name="new-password"
                type="password"
                autoComplete="new-password"
                required
                className="input-field"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="mb-1 block text-sm font-medium text-on-surface">
                Confirm new password
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                className="input-field"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary flex w-full justify-center py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? <LoadingSpinner size="sm" /> : 'Update password'}
          </button>
        </form>
      )}

      <div className="auth-subpanel mt-6 p-4 text-sm leading-6 text-on-surface-variant">
        <Link to="/login" className="font-semibold hover:underline" style={{ color: 'var(--primary)' }}>
          Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
}

export default ResetPasswordPage;
